import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePodcastPlayer } from "../../podcasts/playerStore";

function formatTime(value) {
  const total = Math.max(0, Math.floor(Number(value) || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function resolveProgress(currentTime, duration) {
  const total = Number(duration);
  if (!Number.isFinite(total) || total <= 0) return 0;
  const current = Math.max(0, Math.min(Number(currentTime) || 0, total));
  return Math.round((current / total) * 1000) / 10;
}

const SPEEDS = [1, 1.25, 1.5, 2];

export default function GlobalPodcastPlayer() {
  const navigate = useNavigate();
  const location = useLocation();
  const [queueOpen, setQueueOpen] = React.useState(false);
  const {
    visible,
    track,
    isPlaying,
    isLoading,
    error,
    currentTime,
    duration,
    volume,
    playbackRate,
    queue,
    queueIndex,
    showAcrossApp,
    canPlayPrev,
    canPlayNext,
    playQueueIndex,
    playNextTrack,
    playPrevTrack,
    removeFromQueue,
    clearQueue,
    setShowAcrossApp,
    togglePlay,
    pause,
    seekBy,
    seekTo,
    setVolume,
    setPlaybackRate,
    closePlayer,
    clearError,
  } = usePodcastPlayer();

  const pathname = String(location?.pathname || "");
  const isPodcastArea = pathname.startsWith("/podcasts") || pathname.startsWith("/dashboard/podcasts");
  const shouldRender = Boolean(visible && track && (isPodcastArea || showAcrossApp));

  React.useEffect(() => {
    if (showAcrossApp || isPodcastArea) return;
    if (isPlaying) pause();
  }, [isPodcastArea, isPlaying, pause, showAcrossApp]);

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.toggle("podcasts-player-active", shouldRender);
    return () => {
      document.body.classList.remove("podcasts-player-active");
    };
  }, [shouldRender]);

  if (!shouldRender) return null;

  const progress = resolveProgress(currentTime, duration || track.durationSeconds);
  const totalTime = duration || track.durationSeconds;

  const handleOpenEpisode = () => {
    if (!track?.episodeId) return;
    navigate(`/podcasts/episodes/${encodeURIComponent(String(track.episodeId))}`);
  };

  const cycleSpeed = () => {
    const currentIndex = SPEEDS.indexOf(Number(playbackRate));
    const next = SPEEDS[(currentIndex + 1) % SPEEDS.length];
    setPlaybackRate(next);
  };

  const handlePrev = () => {
    void playPrevTrack();
  };

  const handleNext = () => {
    void playNextTrack();
  };

  const handleQueueItemPlay = (index) => {
    void playQueueIndex(index, { autoplay: true });
  };

  return (
    <section className="podcasts-global-player" aria-label="Плеер подкаста">
      <div className="podcasts-global-player__left">
        <button
          type="button"
          className="podcasts-global-player__cover"
          title="Открыть эпизод"
          onClick={handleOpenEpisode}
        >
          {track.coverImageUrl ? (
            <img src={track.coverImageUrl} alt={track.title} />
          ) : (
            <span>🎙️</span>
          )}
        </button>

        <div className="podcasts-global-player__meta">
          <button type="button" className="podcasts-global-player__title" onClick={handleOpenEpisode}>
            {track.title}
          </button>
          <div className="podcasts-global-player__podcast">
            {track.podcastTitle}
            {track.isAegis ? <span className="podcasts-global-player__aegis">Aegis</span> : null}
          </div>
        </div>

        <button
          type="button"
          className="pgp-close pgp-close--mobile"
          onClick={closePlayer}
          aria-label="Закрыть плеер"
          title="Закрыть"
        >
          ✕
        </button>
      </div>

      <div className="podcasts-global-player__center">
        <div className="podcasts-global-player__controls">
          <button
            type="button"
            className="pgp-btn"
            onClick={handlePrev}
            disabled={!canPlayPrev}
            aria-label="Предыдущий эпизод"
            title="Предыдущий эпизод"
          >
            ⏮
          </button>
          <button type="button" className="pgp-btn" onClick={() => seekBy(-15)} aria-label="Назад 15 секунд">
            -15
          </button>
          <button
            type="button"
            className={`pgp-btn pgp-btn--play ${isPlaying ? "is-playing" : ""}`}
            onClick={togglePlay}
            aria-label={isPlaying ? "Пауза" : "Воспроизвести"}
          >
            {isLoading ? "…" : isPlaying ? "❚❚" : "▶"}
          </button>
          <button type="button" className="pgp-btn" onClick={() => seekBy(30)} aria-label="Вперед 30 секунд">
            +30
          </button>
          <button
            type="button"
            className="pgp-btn"
            onClick={handleNext}
            disabled={!canPlayNext}
            aria-label="Следующий эпизод"
            title="Следующий эпизод"
          >
            ⏭
          </button>
        </div>

        <div className="podcasts-global-player__progress-wrap">
          <span>{formatTime(currentTime)}</span>
          <input
            className="podcasts-global-player__progress"
            type="range"
            min={0}
            max={Math.max(1, Number(totalTime) || 1)}
            step={0.1}
            value={Math.min(Number(currentTime) || 0, Math.max(1, Number(totalTime) || 1))}
            onChange={(e) => seekTo(Number(e.target.value) || 0)}
            aria-label="Прогресс"
            style={{ "--pgp-progress": `${progress}%` }}
          />
          <span>{formatTime(totalTime)}</span>
        </div>
      </div>

      <div className="podcasts-global-player__right">
        <button
          type="button"
          className={`pgp-queue-btn ${queueOpen ? "is-open" : ""}`}
          onClick={() => setQueueOpen((prev) => !prev)}
          title="Очередь"
          aria-label="Очередь"
        >
          ☰ {Array.isArray(queue) ? queue.length : 0}
        </button>

        <label className="pgp-global-toggle" title="Показывать плеер на всех страницах">
          <input
            type="checkbox"
            checked={Boolean(showAcrossApp)}
            onChange={(event) => setShowAcrossApp(Boolean(event.target.checked))}
          />
          <span>Везде</span>
        </label>

        <div className="podcasts-global-player__volume-wrap" title="Громкость">
          <span>🔊</span>
          <input
            className="podcasts-global-player__volume"
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={Number(volume) || 0}
            onChange={(e) => setVolume(Number(e.target.value) || 0)}
            aria-label="Громкость"
          />
        </div>

        <button type="button" className="pgp-speed" onClick={cycleSpeed} title="Скорость воспроизведения">
          {Number(playbackRate).toFixed(Number(playbackRate) % 1 === 0 ? 0 : 2).replace(/\.00$/, "")}x
        </button>

        <button
          type="button"
          className="pgp-close"
          onClick={closePlayer}
          aria-label="Закрыть плеер"
          title="Закрыть"
        >
          ✕
        </button>
      </div>

      {error ? (
        <button type="button" className="podcasts-global-player__error" onClick={clearError}>
          {error}
        </button>
      ) : null}

      {queueOpen ? (
        <aside className="podcasts-global-player__queue" aria-label="Очередь воспроизведения">
          <header className="podcasts-global-player__queue-head">
            <strong>Очередь</strong>
            <div className="podcasts-global-player__queue-actions">
              <button type="button" onClick={clearQueue} disabled={!queue?.length}>
                Очистить
              </button>
              <button type="button" onClick={() => setQueueOpen(false)}>
                ✕
              </button>
            </div>
          </header>
          <div className="podcasts-global-player__queue-list">
            {!queue?.length ? (
              <div className="podcasts-global-player__queue-empty">
                Очередь пока пустая. Запустите несколько подкастов, и они сохранятся локально.
              </div>
            ) : (
              queue.map((item, index) => {
                const active = index === queueIndex;
                return (
                  <div key={`${item.episodeId || index}-${item.audioUrl || item.title}`} className={`pgp-queue-item ${active ? "is-active" : ""}`}>
                    <button type="button" className="pgp-queue-item__main" onClick={() => handleQueueItemPlay(index)}>
                      <span className="pgp-queue-item__title">{item.title || "Эпизод"}</span>
                      <span className="pgp-queue-item__meta">{item.podcastTitle || "Подкаст"}</span>
                    </button>
                    <button
                      type="button"
                      className="pgp-queue-item__remove"
                      onClick={() => removeFromQueue(index)}
                      title="Убрать из очереди"
                      aria-label="Убрать из очереди"
                    >
                      ✕
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </aside>
      ) : null}
    </section>
  );
}
